import { clientNoCdn as client } from '../sanity'
// Types for this module are declared below; no imports required

/**
 * Fetch all products for a specific channel
 */
export const getProductsByChannel = async (
  channelId: string
): Promise<ProductListItem[]> => {
  return client.fetch(
    `*[_type == "product" && !(_id match "drafts.*") && relations.relatedDomain->channel._ref == $channelId] | order(_updatedAt desc) {
      _id,
      _type,
      sku,
      title,
      description,
      rrp,
      channelMapping[] {
        channel,
        configSet {
          _ref
        }
      },
      variants,
      _createdAt,
      _updatedAt
    }`,
    { channelId }
  )
}

/**
 * Count products for a specific channel
 */
export const getProductsCountByChannel = async (channelId: string): Promise<number> => {
  return client.fetch(
    `count(*[_type == "product" && !(_id match "drafts.*") && relations.relatedDomain->channel._ref == $channelId])`,
    { channelId }
  )
}

/**
 * Count products for all channels at once
 */
export interface ChannelCount {
  _id: string
  name?: string
  count: number
}

export const getProductCountsByChannel = async (): Promise<ChannelCount[]> => {
  return client.fetch(
    `*[_type == "channel"] | order(name asc) {
      _id,
      name,
      "count": count(*[
        _type == "product" &&
        !(_id match "drafts.*") &&
        relations.relatedDomain->channel._ref == ^._id
      ])
    }`
  )
}

/**
 * Product detail type - matches actual Sanity schema with all related data
 */
export interface ProductDetail {
  _id: string
  _type: 'product'
  title: string
  sku: string
  rrp: number
  cost?: number
  isPrintOnDemand?: boolean
  description?: any[] // Portable text array
  relations?: {
    relatedBundle?: {
      _ref: string
      _id: string
      title?: string
      sku?: string
    }
    relatedDomain?: {
      _ref: string
    }
  }
  channelMapping?: Array<{
    channel: string
    status?: string
    configSet?: {
      _ref: string
      name?: string
    }
  }>
  media?: {
    galleryImages?: Array<{
      asset?: {
        _ref: string
      }
      url?: string
    }>
    hero?: {
      asset?: {
        _ref: string
      }
      url?: string
    }
    pod?: {
      asset?: {
        _ref: string
      }
      url?: string
    }
  }
  categories?: Array<{
    _ref: string
  }>
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch a single product by ID with all detail fields
 */
export const getProductById = async (id: string): Promise<ProductDetail | null> => {
  return client.fetch(
    `*[_type == "product" && _id == $id][0] {
      _id,
      _type,
      title,
      sku,
      rrp,
      cost,
      isPrintOnDemand,
      description,
      relations {
        relatedBundle-> {
          _id,
          _ref,
          title,
          sku
        },
        relatedDomain {
          _ref
        }
      },
      channelMapping[] {
        channel,
        status,
        configSet-> {
          _ref,
          name
        }
      },
      media {
        galleryImages[] {
          asset {
            _ref
          },
          "url": asset->url
        },
        hero {
          asset {
            _ref
          },
          "url": asset->url
        },
        pod {
          asset {
            _ref
          },
          "url": asset->url
        }
      },
      categories[] {
        _ref
      },
      _createdAt,
      _updatedAt
    }`,
    { id }
  )
}

/**
 * Product using bundle - simplified type for bundle detail page
 */
export interface ProductUsingBundle {
  _id: string
  _type: 'product'
  title: string
  sku: string
  rrp?: number
  channelMapping?: Array<{
    channel: string
    status?: string
  }>
}

/**
 * Product list item type - matches actual Sanity schema
 */
export interface ProductListItem {
  _id: string
  _type: 'product'
  title: string
  sku: string
  rrp: number
  isPrintOnDemand?: boolean
  relations?: {
    relatedBundle?: {
      _ref: string
      title?: string
      sku?: string
    }
  }
  channelMapping?: Array<{
    channel: string
    status?: string
  }>
  media?: {
    galleryImages?: Array<{
      asset?: {
        _ref: string
      }
      url?: string
    }>
    hero?: {
      asset?: {
        _ref: string
      }
      url?: string
    }
  }
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch all products for list display
 * Filters out draft products (IDs prefixed with "drafts.")
 */
export const getAllProducts = async (): Promise<ProductListItem[]> => {
  return client.fetch(
    `*[_type == "product" && !(_id match "drafts.*")] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      sku,
      rrp,
      isPrintOnDemand,
      relations {
        relatedBundle-> {
          _id,
          _ref,
          title,
          sku
        }
      },
      channelMapping[] {
        channel,
        status
      },
      media {
        galleryImages[] {
          asset {
            _ref
          },
          "url": asset->url
        },
        hero {
          asset {
            _ref
          },
          "url": asset->url
        }
      },
      _createdAt,
      _updatedAt
    }`
  )
}

/**
 * Fetch products that use a specific stock bundle
 * Filters out draft products (IDs prefixed with "drafts.")
 */
export const getProductsByBundleId = async (bundleId: string): Promise<ProductUsingBundle[]> => {
  return client.fetch(
    `*[_type == "product" && relations.relatedBundle._ref == $bundleId && !(_id match "drafts.*")] {
      _id,
      _type,
      title,
      sku,
      rrp,
      channelMapping[] {
        channel,
        status
      }
    } | order(title asc)`,
    { bundleId }
  )
}

/**
 * Check whether a given SKU is unique across non-draft products.
 */
export const isSkuUnique = async (sku: string): Promise<boolean> => {
  const trimmed = sku.trim()
  if (!trimmed) return false

  const count = await client.fetch(
    `count(*[_type == "product" && !(_id match "drafts.*") && sku == $sku])`,
    { sku: trimmed }
  )

  return Number(count) === 0
}
